import { useRef, useState } from 'react';
import { Phone } from 'lucide-react';
import { useQueryClient } from '@tanstack/react-query';
import { Icon, Skeleton, Toggle } from '../../../../ds/components';
import { clsx } from '../../../../ds/clsx';
import { ApiError, errorMessage } from '../../../../lib/api';
import { MobileWizardFrame, WizardButton, WizardField, WizardSelect } from '../../ui';
import { organizationKeys, uploadWithProgress, useBrand, useCompany } from '../api';
import { ColorSwatches } from '../BrandParts';
import { useCompanyForm } from '../companyForm';
import { LoadError } from '../LoadError';
import { SecurityStep } from '../steps/SecurityStep';
import { useSaveColor } from '../steps/BrandStep';
import { sendLabel, useTeamInvites } from '../teamInvites';
import type { Brand, Company } from '../types';
import { useWizardNav } from '../wizardContext';
import styles from './MobileSteps.module.css';

const TOTAL = 6;

/** Admin M2 · Marke: the light logo from gallery or camera, the dark one „am Rechner nachholen", accent colour. */
export function MobileBrandStep() {
  const nav = useWizardNav();
  const queryClient = useQueryClient();
  const brand = useBrand();
  const color = useSaveColor();
  const gallery = useRef<HTMLInputElement>(null);
  const camera = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [pending, setPending] = useState<'skip' | 'continue' | null>(null);

  async function upload(file: File) {
    setError(null);
    setUploading(0);
    const form = new FormData();
    form.append('file', file);
    try {
      const next = await uploadWithProgress<Brand>(
        '/settings/organization/brand/logo/light',
        form,
        setUploading,
      );
      queryClient.setQueryData(organizationKeys.brand, next);
    } catch (failure) {
      setError(failure instanceof Error ? failure.message : errorMessage(failure));
    } finally {
      setUploading(null);
    }
  }

  async function leave(action: 'skip' | 'continue') {
    setPending(action);
    setError(null);
    try {
      await nav.complete('brand', action, 'company');
    } catch (failure) {
      setError(errorMessage(failure));
    } finally {
      setPending(null);
    }
  }

  const light = brand.data?.logos.light ?? null;
  const input = (ref: typeof gallery, capture: boolean) => (
    <input
      ref={ref}
      type="file"
      accept="image/svg+xml,image/png,.svg,.png"
      capture={capture ? 'environment' : undefined}
      className={styles.fileInput}
      tabIndex={-1}
      aria-hidden="true"
      onChange={(event) => {
        const file = event.target.files?.[0];
        event.target.value = '';
        if (file) void upload(file);
      }}
    />
  );

  return (
    <MobileWizardFrame
      title="Marke"
      stepLabel={`Schritt 1 von ${TOTAL}`}
      segments={TOTAL}
      current={1}
      footer={
        <>
          <WizardButton
            variant="ghost"
            size="mobile"
            loading={pending === 'skip'}
            onClick={() => void leave('skip')}
          >
            Später
          </WizardButton>
          <WizardButton
            variant="primary"
            size="mobile"
            className={styles.grow}
            loading={pending === 'continue'}
            onClick={() => void leave('continue')}
          >
            Weiter
          </WizardButton>
        </>
      }
    >
      <p className={styles.lead}>
        Logo aus der Galerie oder per Foto vom Briefkopf. Feineinstellungen später am Rechner.
      </p>
      {brand.isPending ? (
        <Skeleton variant="block" height={200} />
      ) : brand.isError ? (
        <LoadError
          what="Die Marke konnte nicht geladen werden"
          error={brand.error}
          onRetry={() => void brand.refetch()}
        />
      ) : (
        <>
          <section className={styles.card} aria-label="Hell · auf Papier">
            <span className={styles.eyebrow}>Hell · auf Papier</span>
            <div className={styles.logoBox}>
              {uploading !== null ? (
                `Wird hochgeladen … ${Math.round(uploading * 100)} %`
              ) : light && light.scanStatus === 'clean' ? (
                <img className={styles.logoImage} src={light.url} alt="Logo hell" />
              ) : light ? (
                `${light.name} · wird geprüft`
              ) : (
                'Noch kein Logo'
              )}
            </div>
            <div className={styles.buttons}>
              <WizardButton
                variant="secondary"
                className={styles.half}
                onClick={() => gallery.current?.click()}
              >
                Galerie
              </WizardButton>
              <WizardButton
                variant="secondary"
                className={styles.half}
                onClick={() => camera.current?.click()}
              >
                Foto
              </WizardButton>
            </div>
            {input(gallery, false)}
            {input(camera, true)}
          </section>
          <section className={clsx(styles.card, styles.cardDashed)} aria-label="Dunkel · auf Navy">
            <span className={styles.eyebrow}>Dunkel · auf Navy</span>
            <span className={styles.subtle}>
              {brand.data.logos.dark
                ? brand.data.logos.dark.name
                : 'Noch nicht hinterlegt · am Rechner nachholen'}
            </span>
          </section>
          <section className={styles.card} aria-label="Akzentfarbe">
            <span className={styles.title}>Akzentfarbe</span>
            <ColorSwatches
              presets={brand.data.colorPresets}
              value={brand.data.color}
              onPick={(value) => void color.save(value)}
            />
          </section>
        </>
      )}
      {error || color.error ? (
        <span className={styles.error} role="alert">
          {error ?? color.error}
        </span>
      ) : null}
    </MobileWizardFrame>
  );
}

/** Firmendaten on a phone (no frame): the eight fields of Admin 04 in one column. */
export function MobileCompanyStep() {
  const company = useCompany();
  return (
    <MobileWizardFrame title="Firmendaten" stepLabel={`Schritt 2 von ${TOTAL}`} segments={TOTAL} current={2}>
      {company.isPending ? (
        <Skeleton variant="block" height={420} />
      ) : company.isError ? (
        <LoadError
          what="Die Firmendaten konnten nicht geladen werden"
          error={company.error}
          onRetry={() => void company.refetch()}
        />
      ) : (
        <MobileCompanyForm company={company.data} />
      )}
    </MobileWizardFrame>
  );
}

function MobileCompanyForm({ company }: { company: Company }) {
  const nav = useWizardNav();
  const form = useCompanyForm(company);
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const confirmed = company.vatCheck.status === 'valid' && form.values.vatId.trim() === (company.vatId ?? '');

  async function next() {
    setPending(true);
    setError(null);
    try {
      await form.settle();
      await nav.complete('company_data', 'continue', 'legal-texts');
    } catch (failure) {
      setError(
        failure instanceof ApiError ? (failure.fields['company'] ?? failure.message) : errorMessage(failure),
      );
    } finally {
      setPending(false);
    }
  }

  const text = (
    field:
      'companyName' | 'street' | 'postalCodeCity' | 'register' | 'managingDirectors' | 'supervisoryAuthority',
  ) => ({
    value: form.values[field],
    onChange: (event: { target: { value: string } }) => form.set(field, event.target.value),
    onBlur: () => void form.commit(field),
    error: form.errors[field],
  });
  const invalid = company.vatCheck.status === 'invalid' && form.values.vatId.trim() === (company.vatId ?? '');
  return (
    <>
      <p className={styles.lead}>
        Diese Angaben stehen im Impressum, auf Rechnungen und in Mails an Mandanten.
      </p>
      <div className={styles.fields}>
        <WizardField label="Firmenname laut Handelsregister" {...text('companyName')} />
        <WizardSelect
          label="Rechtsform"
          value={form.values.legalForm}
          options={[
            ...(form.values.legalForm === '' ? [{ value: '', label: 'Bitte wählen' }] : []),
            ...company.legalForms.map((option) => ({ value: option, label: option })),
          ]}
          onChange={(event) => {
            form.set('legalForm', event.target.value);
            void form.commit('legalForm', event.target.value);
          }}
          error={form.errors.legalForm}
        />
        <WizardField label="Straße und Hausnummer" {...text('street')} />
        <WizardField label="PLZ und Ort" {...text('postalCodeCity')} />
        <WizardField label="Registergericht und Nummer" {...text('register')} />
        <WizardField
          label={invalid ? 'Umsatzsteuer-ID · nicht bestätigt' : 'Umsatzsteuer-ID'}
          value={form.values.vatId}
          onChange={(event) => form.set('vatId', event.target.value)}
          onBlur={() => void form.commit('vatId')}
          error={form.errors.vatId ?? (invalid ? company.vatCheck.label : undefined)}
        />
        <WizardField label="Geschäftsführung" {...text('managingDirectors')} />
        <WizardField label="Aufsichtsbehörde · § 34c GewO" {...text('supervisoryAuthority')} />
      </div>
      {confirmed ? null : (
        <span className={styles.hint}>Ohne bestätigte USt-ID wird mit Umsatzsteuer abgerechnet.</span>
      )}
      {(error ?? form.failure) ? (
        <span className={styles.error} role="alert">
          {error ?? form.failure}
        </span>
      ) : null}
      <div className={styles.buttons}>
        <WizardButton variant="ghost" size="mobile" onClick={() => nav.go('brand')}>
          Zurück
        </WizardButton>
        <WizardButton
          variant="primary"
          size="mobile"
          className={styles.grow}
          loading={pending}
          onClick={() => void next()}
        >
          {confirmed ? 'Weiter' : 'Trotzdem weiter'}
        </WizardButton>
      </div>
    </>
  );
}

const statusWords = { done: 'Erledigt', skipped: 'Übersprungen', open: 'Am Rechner fortsetzen' } as const;

/** Admin M3 · „Am Rechner fortsetzen": legal texts, domain and telephony wait for the computer. */
export function MobileContinueStep() {
  const nav = useWizardNav();
  const points = nav.wizard.steps.filter((step) => step.key !== 'team' && step.key !== 'payment_method');
  return (
    <MobileWizardFrame
      title="Einrichtung"
      onBack={() => nav.go('company')}
      footer={
        <WizardButton variant="primary" size="mobile" className={styles.grow} onClick={() => nav.go('team')}>
          Weiter zum Team
        </WizardButton>
      }
    >
      <section className={styles.navyCard} aria-labelledby="continue-title">
        <span className={styles.navyEyebrow}>Drei Schritte brauchen den Rechner</span>
        <h2 id="continue-title" className={styles.navyTitle}>
          Rechtstexte, Domain und Telefonie
        </h2>
        <p className={styles.navyText}>Dafür brauchen Sie PDFs, DNS-Zugang und ein Twilio-Konto.</p>
      </section>
      <ul className={styles.list}>
        {points.map((step) => (
          <li key={step.key} className={styles.item}>
            <span
              className={clsx(
                styles.itemIcon,
                step.status === 'done' && styles.iconDone,
                step.status === 'skipped' && styles.iconSkipped,
              )}
              aria-hidden="true"
            >
              {step.status === 'done' ? (
                '✓'
              ) : step.status === 'skipped' ? (
                '!'
              ) : step.key === 'telephony' ? (
                <Icon icon={Phone} size={16} strokeWidth={1.75} />
              ) : (
                '◷'
              )}
            </span>
            <span className={styles.itemText}>
              <span className={styles.itemTitle}>{step.label}</span>
              <span className={styles.itemSub}>{statusWords[step.status]}</span>
            </span>
          </li>
        ))}
      </ul>
      <div className={styles.note}>
        Sie können DAMOKLES schon nutzen: Objekte anlegen, Kontakte pflegen, Termine führen.
      </div>
    </MobileWizardFrame>
  );
}

/** Admin M4 · Team: one card per person (address, role, own billing), the seat line. */
export function MobileTeamStep() {
  const nav = useWizardNav();
  const team = useTeamInvites();
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function send() {
    setError(null);
    if (!(await team.send())) return;
    try {
      await nav.complete('team', 'continue', 'security');
    } catch (failure) {
      setError(errorMessage(failure));
    }
  }

  async function alone() {
    setPending(true);
    setError(null);
    try {
      await nav.complete('team', 'skip', 'security');
    } catch (failure) {
      setError(errorMessage(failure));
    } finally {
      setPending(false);
    }
  }

  const roles = team.roles.data?.items ?? [];
  return (
    <MobileWizardFrame
      title="Team"
      stepLabel={`Schritt 6 von ${TOTAL}`}
      segments={TOTAL}
      current={6}
      onBack={() => nav.go('legal-texts')}
      footer={
        <>
          <WizardButton variant="ghost" size="mobile" loading={pending} onClick={() => void alone()}>
            Allein starten
          </WizardButton>
          <WizardButton
            variant="primary"
            size="mobile"
            className={styles.grow}
            disabled={team.count === 0 || team.blocked}
            loading={team.sending}
            onClick={() => void send()}
          >
            {sendLabel(team.count)}
          </WizardButton>
        </>
      }
    >
      <p className={styles.lead}>
        Einladungen sind sieben Tage gültig. Abrechnung pro Nutzer hier umschaltbar.
      </p>
      {team.rows.map((row, index) => {
        const state = team.states[index] ?? { error: null, hint: null, valid: false };
        const position = index + 1;
        return (
          <section key={row.id} className={styles.personCard} aria-label={`Person ${position}`}>
            <input
              className={clsx(styles.mobileInput, state.error && styles.mobileInputInvalid)}
              type="email"
              value={row.email}
              placeholder="name@firma.de"
              aria-label={`E-Mail-Adresse ${position}`}
              aria-invalid={state.error ? true : undefined}
              onChange={(event) => team.update(row.id, { email: event.target.value })}
            />
            {state.error ? (
              <span className={styles.error} role="alert">
                {state.error}
              </span>
            ) : state.hint ? (
              <span className={styles.hint}>{state.hint}</span>
            ) : null}
            <div className={styles.selectWrap}>
              <select
                className={styles.select}
                aria-label={`Rolle ${position}`}
                value={team.roleOf(row)}
                onChange={(event) => team.update(row.id, { roleId: event.target.value })}
              >
                {roles.map((role) => (
                  <option key={role.id} value={role.id}>
                    {role.name}
                  </option>
                ))}
              </select>
              <span className={styles.chevron} aria-hidden="true">
                ⌄
              </span>
            </div>
            <div className={styles.personFoot}>
              <Toggle
                size="large"
                checked={row.ownBilling}
                onChange={(checked) => team.update(row.id, { ownBilling: checked })}
                label={`Eigene Abrechnung ${position}`}
                labelHidden
              />
              <span className={clsx(styles.billing, row.ownBilling && styles.billingOn)} aria-hidden="true">
                {row.ownBilling ? 'selbst zahlend' : 'über Firma'}
              </span>
              <button type="button" className={styles.removePerson} onClick={() => team.remove(row.id)}>
                Entfernen
              </button>
            </div>
          </section>
        );
      })}
      <button type="button" className={clsx('pressable', styles.add)} onClick={team.add}>
        + Weitere Person
      </button>
      {team.seatLabel ? <div className={styles.note}>{team.seatLabel}</div> : null}
      {(error ?? team.failure) ? (
        <span className={styles.error} role="alert">
          {error ?? team.failure}
        </span>
      ) : null}
    </MobileWizardFrame>
  );
}

/** The 2FA on a phone (no frame): the setup panel in the phone frame. */
export function MobileSecurityStep() {
  const nav = useWizardNav();
  return (
    <MobileWizardFrame title="Zweiter Faktor" onBack={() => nav.go('team')}>
      <SecurityStep compact />
    </MobileWizardFrame>
  );
}
